import { useEffect, useMemo, useState } from 'react';
import { Link, Navigate, useNavigate, useParams } from 'react-router-dom';
import { getEnigma } from '../data/enigmas';
import { useProgress } from '../hooks/useProgress';
import { checkAnswer, checkMultiAnswer } from '../utils/normalize';
import seloLogo from '../../Images/seloLogo.png';

export default function EnigmaPage() {
  const { id = '' } = useParams();
  const enigma = getEnigma(id);
  const { isSolved, markSolved } = useProgress();
  const navigate = useNavigate();
  const isMulti = Boolean(enigma?.fields?.length);

  const [value, setValue] = useState('');
  const [values, setValues] = useState({});
  const [result, setResult] = useState(null);
  const [message, setMessage] = useState('');
  const [shake, setShake] = useState(false);

  useEffect(() => {
    setValue('');
    setResult(null);
    setMessage('');
    setShake(false);
    if (enigma?.fields) {
      setValues(Object.fromEntries(enigma.fields.map((f) => [f.id, ''])));
    } else {
      setValues({});
    }
  }, [id, enigma]);

  const bodyLines = useMemo(() => {
    if (!enigma?.body?.trim()) return [];
    return enigma.body.trim().split(/\n/);
  }, [enigma]);

  if (!enigma) {
    return <Navigate to="/festa-historia/mapa" replace />;
  }

  if (isSolved(enigma.id)) {
    return (
      <main className="fh-page fh-page--enigma">
        <div className="fh-shell">
          <p className="fh-eyebrow">Enigma {enigma.number}</p>
          <h1 className="fh-feedback fh-feedback--correct">Já resolvido</h1>
          <p className="fh-lede">Este local já foi desvendado neste aparelho.</p>
          <Link className="fh-btn fh-btn--primary" to="/festa-historia/mapa">
            Voltar ao mapa
          </Link>
        </div>
      </main>
    );
  }

  function finishCorrect() {
    markSolved(enigma.id);
    //window.setTimeout(() => {
      //navigate('/festa-historia/mapa');
    //}, 3400);
  }

  function onSubmit(e) {
    e.preventDefault();

    const outcome = isMulti
      ? checkMultiAnswer(values, enigma.fields)
      : checkAnswer(
          value,
          enigma.answer,
          enigma.acceptedAnswers,
          enigma.nearMisses,
        );

    setResult(outcome.status);
    setMessage(outcome.message);

    if (outcome.status === 'correct') {
      finishCorrect();
      return;
    }

    setShake(true);
    window.setTimeout(() => setShake(false), 450);
  }

  const showBody = bodyLines.length > 0;
  const title = enigma.title?.trim() || '';
  /** Por agora numerado; mais tarde será o nome do local */
  const placeLabel = enigma.locationName?.trim() || `Enigma ${enigma.number}`;

  return (
    <main className="fh-page fh-page--enigma">
      <Link to="/festa-historia/mapa" className="fh-back fh-back--float">
        ← Mapa
      </Link>

      <div className={`fh-shell ${shake ? 'fh-shell--shake' : ''}`}>
        <p className="fh-eyebrow">{placeLabel}</p>
        {title ? <h1 className="fh-enigma-prompt">{title}</h1> : null}

        {showBody ? (
          <div className="fh-enigma-body">
            {bodyLines.map((line, i) =>
              line.trim() === '' ? (
                <br key={`b-${i}`} />
              ) : (
                <p key={`l-${i}`}>{line}</p>
              ),
            )}
          </div>
        ) : isMulti ? (
          <p className="fh-lede fh-lede--compact">
            Preenche os três espaços com a solução do enigma.
          </p>
        ) : null}

        <form className="fh-answer-form" onSubmit={onSubmit}>
          {isMulti ? (
            enigma.fields.map((field) => (
              <div className="fh-field" key={field.id}>
                <label className="fh-field__label" htmlFor={field.id}>
                  {field.label}
                </label>
                <input
                  id={field.id}
                  className="fh-answer-input"
                  type="text"
                  autoComplete="off"
                  autoCorrect="off"
                  autoCapitalize="none"
                  spellCheck={false}
                  enterKeyHint="next"
                  placeholder={field.label}
                  value={values[field.id] || ''}
                  onChange={(ev) => {
                    setValues((prev) => ({
                      ...prev,
                      [field.id]: ev.target.value,
                    }));
                    setResult(null);
                    setMessage('');
                  }}
                />
              </div>
            ))
          ) : (
            <>
              <label className="fh-sr-only" htmlFor="resposta">
                Resposta
              </label>
              <input
                id="resposta"
                className="fh-answer-input"
                type="text"
                autoComplete="off"
                autoCorrect="off"
                autoCapitalize="none"
                spellCheck={false}
                enterKeyHint="done"
                placeholder="Escreve a resposta…"
                value={value}
                onChange={(ev) => {
                  setValue(ev.target.value);
                  setResult(null);
                  setMessage('');
                }}
              />
            </>
          )}

          <button className="fh-btn fh-btn--primary" type="submit">
            Validar
          </button>
        </form>

        {result && (
          <p
            className={`fh-feedback fh-feedback--${result}`}
            role="status"
            aria-live="polite"
          >
            {message}
          </p>
        )}
      </div>

      <img
        src={seloLogo}
        alt="Escape Room Bragança"
        className="fh-enigma-selo"
      />
    </main>
  );
}
